import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/features/auth';
import { useLabels } from '@/features/tasks/api';

const LABEL_COLOURS = ['#C2413A', '#C9762B', '#4A7CB5', '#2F6F4E', '#7C5CB5', '#7C838A'];

export function LabelsPage() {
  const can = useCan();
  const qc = useQueryClient();
  const { data: labels, isLoading } = useLabels();

  const [name, setName] = useState('');
  const [colour, setColour] = useState(LABEL_COLOURS[0]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['labels'] });

  const create = useMutation({
    mutationFn: async () => api.post('/labels', { name: name.trim(), colour }),
    onSuccess: () => {
      setName('');
      invalidate();
      toast.success('Label created');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not create the label'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) =>
      (await api.delete<{ tasksUnlabelled: number }>(`/labels/${id}`)).data,
    onSuccess: (result) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success(`Label deleted — removed from ${result.tasksUnlabelled} tasks`);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the label'),
  });

  const canManage = can(PERMISSIONS.LABEL_MANAGE);

  return (
    <>
      <PageHeader
        title="Labels"
        subtitle="Workspace-wide. Applying one to a task is part of editing that task."
      />

      <div className="max-w-[560px] p-4">
        {canManage && (
          <div className="vt-surface mb-3 flex flex-col gap-3 p-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="label-name">New label</Label>
              <div className="flex gap-2">
                <Input
                  id="label-name"
                  value={name}
                  placeholder="needs-regression-test"
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && name.trim()) create.mutate();
                  }}
                />
                <Button
                  variant="primary"
                  onClick={() => create.mutate()}
                  disabled={!name.trim() || create.isPending}
                >
                  {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
                  Add
                </Button>
              </div>
            </div>
            <div className="flex gap-1.5">
              {LABEL_COLOURS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  aria-pressed={colour === c}
                  onClick={() => setColour(c)}
                  className="size-6 rounded-full border-2 transition-transform duration-[120ms] hover:scale-110"
                  style={{ background: c, borderColor: colour === c ? 'var(--text)' : 'transparent' }}
                />
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-32" />
        ) : !labels || labels.length === 0 ? (
          <EmptyState
            title="No labels yet"
            description="Labels cut across projects — good for things like needs-regression-test or blocked-on-client."
          />
        ) : (
          <ul className="vt-surface divide-y divide-[var(--border)]">
            {labels.map((label) => (
              <li key={label._id} className="flex items-center gap-2 px-3 py-2 text-[13px]">
                <span className="size-2.5 rounded-full" style={{ background: label.colour }} aria-hidden />
                <span className="flex-1 truncate">{label.name}</span>
                <span className="vt-mono text-[11px] vt-muted">{label.usageCount} tasks</span>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete ${label.name}`}
                    className="text-[var(--danger)]"
                    onClick={() => {
                      if (
                        label.usageCount > 0 &&
                        !window.confirm(
                          `“${label.name}” is on ${label.usageCount} task(s). Delete it and remove it from all of them?`,
                        )
                      ) {
                        return;
                      }
                      remove.mutate(label._id);
                    }}
                  >
                    <Trash2 />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
